import type { TeamSettings } from "@/lib/types";

/**
 * Pure helpers behind Coach > Settings' save flow.
 *
 * `diffTeamSettings` returns only the keys whose value changed
 * between the server payload and the local edit buffer — the
 * PATCH body should carry just those (the backend applies a
 * partial update; sending unchanged keys is wasteful and widens
 * the validation surface). `isTeamSettingsDirty` is the boolean
 * the Save button's enabled state keys off.
 *
 * Equality is by `JSON.stringify` — every `TEAM_SETTING_SCHEMAS`
 * value is JSON-native (bool / string / string[]), so structural
 * stringify-equality is exact. Array values are compared in
 * order; the `SettingChipMulti` control already emits its array
 * in a stable (schema) order, so two equivalent selections
 * stringify identically.
 */

const eq = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

/** The subset of `draft` whose values differ from `server`. */
export function diffTeamSettings(
  server: TeamSettings,
  draft: TeamSettings,
): TeamSettings {
  const changed: TeamSettings = {};
  for (const key of Object.keys(draft) as (keyof TeamSettings)[]) {
    if (!eq(draft[key], server[key])) {
      (changed as Record<string, unknown>)[key] = draft[key];
    }
  }
  return changed;
}

/** True when the draft differs from the server payload in any key. */
export function isTeamSettingsDirty(
  server: TeamSettings,
  draft: TeamSettings,
): boolean {
  return Object.keys(diffTeamSettings(server, draft)).length > 0;
}
